import React, { useState } from 'react';
import { injectIntl } from 'react-intl';

import PropTypes from 'prop-types';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import { ChatCardContainer } from '../../Dashboard/styles';

const DEFAULT_METRICS = [
    {
        value: 'tasks',
        labelId: 'page.robots.cleaning_report.robot_comparison.metrics.tasks',
        fallback: 'Tarefas',
    },
    {
        value: 'time',
        labelId: 'page.robots.cleaning_report.robot_comparison.metrics.time',
        fallback: 'Tempo (h)',
    },
    {
        value: 'area',
        labelId: 'page.robots.cleaning_report.robot_comparison.metrics.area',
        fallback: 'Área (m²)',
    },
    {
        value: 'frequency',
        labelId:
            'page.robots.cleaning_report.robot_comparison.metrics.frequency',
        fallback: 'Frequência (Dias)',
    },
];

function RobotComparisonDashboardCard({
    data = [],
    cardColor = '#8884d8',
    intl,
    extraMetrics = [],
}) {
    const METRICS = [...DEFAULT_METRICS, ...extraMetrics];
    const [selectedMetric, setSelectedMetric] = useState('tasks');

    const activeMetric = METRICS.find(m => m.value === selectedMetric);

    const safeFormatMessage = (id, fallback) => {
        try {
            return intl.formatMessage({ id });
        } catch (e) {
            return fallback;
        }
    };

    const renderChart = () => {
        if (!data || data.length === 0) {
            return (
                <Typography>
                    <LocaleMessage msg="page.robots.cleaning_report.robot_comparison.no_data" />
                </Typography>
            );
        }

        // Calcula uma altura mínima para garantir que não fique muito espremido se houver muitos robôs
        // 60 pixels por barra/robô no mínimo
        const minChartHeight = Math.max(300, data.length * 60);

        return (
            <div
                style={{
                    width: '100%',
                    height: '350px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <div
                    style={{ height: `${minChartHeight}px`, minWidth: '100%' }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            layout="vertical"
                            data={data}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis
                                type="category"
                                dataKey="name"
                                tick={{ fontSize: 11 }}
                                width={100}
                            />
                            <Tooltip
                                formatter={value =>
                                    value != null
                                        ? Number(value).toFixed(2)
                                        : value
                                }
                            />
                            <Legend />
                            <Bar
                                dataKey={selectedMetric}
                                name={safeFormatMessage(
                                    activeMetric.labelId,
                                    activeMetric.fallback
                                )}
                                fill={cardColor}
                                radius={[0, 4, 4, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    return (
        <div className="row card-row">
            <div className="col-12 dash-card mb-5">
                <ChatCardContainer>
                    <ChartCard
                        className="animated slideInFromLeft animation-duration-3"
                        title={safeFormatMessage(
                            'page.robots.cleaning_report.robot_comparison.title',
                            'Comparação de Robôs'
                        )}
                        desc={safeFormatMessage(
                            'page.robots.cleaning_report.robot_comparison.desc',
                            'Compare o desempenho individual dos robôs selecionados.'
                        )}
                        value=""
                    >
                        <div className="row">
                            <div className="col-12 mb-3">
                                <div
                                    className="d-flex align-items-center justify-content-end mb-3"
                                    style={{ gap: '15px' }}
                                >
                                    <FormControl
                                        size="small"
                                        style={{ minWidth: 200 }}
                                    >
                                        <InputLabel>
                                            <LocaleMessage msg="page.robots.cleaning_report.robot_comparison.metric_label" />
                                        </InputLabel>
                                        <Select
                                            value={selectedMetric}
                                            onChange={e =>
                                                setSelectedMetric(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {METRICS.map(m => (
                                                <MenuItem
                                                    key={m.value}
                                                    value={m.value}
                                                >
                                                    {safeFormatMessage(
                                                        m.labelId,
                                                        m.fallback
                                                    )}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                            <div className="col-12 area-chart-container">
                                {renderChart()}
                            </div>
                        </div>
                    </ChartCard>
                </ChatCardContainer>
            </div>
        </div>
    );
}

const robotCompareDatumPropType = PropTypes.shape({
    name: PropTypes.string,
    tasks: PropTypes.number,
    time: PropTypes.number,
    area: PropTypes.number,
    frequency: PropTypes.number,
});

RobotComparisonDashboardCard.propTypes = {
    data: PropTypes.arrayOf(robotCompareDatumPropType),
    cardColor: PropTypes.string,
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    extraMetrics: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            labelId: PropTypes.string,
            fallback: PropTypes.string.isRequired,
        })
    ),
};

RobotComparisonDashboardCard.defaultProps = {
    data: [],
    cardColor: '#8884d8',
    extraMetrics: [],
};

export default injectIntl(RobotComparisonDashboardCard);
